/**
 * Miss row for the citizenship test.
 *
 * Its cards are whole questions, so the shared "term → translation" row would
 * put a paragraph next to a paragraph. Instead: the question, clamped to two
 * lines, then only the two options that matter — the right one and the one you
 * picked. The other two are noise in a scan.
 *
 * This is the compact counterpart to the simulation review
 * (`src/routes/simulation-result.tsx`), which deliberately expands everything
 * because a sim is a teaching moment. A practice session already showed you the
 * full card back after each answer; this list is for re-scanning, not
 * re-learning.
 */
import { Check, X } from 'lucide-react';

import { MissRowFrame } from '@/components/practice/SharedMissRow';
import type { EinbuergerungsPayload, OptionLetter } from '@/core/types';
import { cn } from '@/lib/utils';

import type { MissRowProps } from '../types';

export function MissRow({ card, user_answer }: MissRowProps<EinbuergerungsPayload>) {
  const { options, correct_letter } = card.payload;
  const userLetter = user_answer as OptionLetter | null;
  const correctOption = options.find((o) => o.letter === correct_letter);
  const chosenOption = options.find((o) => o.letter === userLetter);

  return (
    <MissRowFrame>
      <p className="font-display line-clamp-2 text-base font-semibold">
        {card.external_id ? (
          <span className="text-muted-foreground font-sans text-xs font-medium tracking-wider">
            {card.external_id} ·{' '}
          </span>
        ) : null}
        {card.de}
      </p>
      {correctOption ? <OptionLine text={correctOption.de} correct /> : null}
      {chosenOption ? (
        <OptionLine text={chosenOption.de} correct={false} />
      ) : (
        <span className="text-muted-foreground text-sm italic">skipped</span>
      )}
    </MissRowFrame>
  );
}

function OptionLine({ text, correct }: { text: string; correct: boolean }) {
  const Icon = correct ? Check : X;
  return (
    <span
      className={cn(
        'flex items-start gap-1.5 text-sm',
        correct ? 'text-success' : 'text-destructive',
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" />
      <span className={cn(!correct && 'text-muted-foreground')}>{text}</span>
    </span>
  );
}
